// ── Account profile <-> data-manager bridge. ──
// The MMO entry flow resolves a wallet, looks the account up in the api, and seeds the local
// data-manager so the shared world + HUD reflect the real account. This module centralizes that
// mapping (api PlayerView → data-manager keys) and the reverse read used by the profile page.

import { DATA_MANAGER_STORE_KEYS, dataManager } from '../utils/data-manager.js';
import { resolveSkinKey } from './characters/skins.js';
import { netManager } from '../net/index.js';

/** localStorage key recording which wallet the locally-saved party belongs to (anti cross-wallet bleed). */
const PARTY_OWNER_KEY = 'mn_party_owner';

/**
 * Seed the data-manager from an api account view (existing player) so the world/HUD render the
 * real account. Skin is derived from the stored `avatar` (a PLAYER_SKIN_* key). Party is left to
 * the local save (the battle system uses full local Monster objects); gold/name/avatar/bio sync
 * from the authoritative api row. Persists to localStorage so a reload keeps the synced profile.
 * @param {import('../net/index.js').PlayerView} view
 * @returns {void}
 */
export function applyAccountToDataManager(view) {
  const player = view?.player;
  if (!player) return;

  const skin = resolveSkinKey(player.avatar);
  dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_NAME, player.username ?? '');
  dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_AVATAR, skin);
  dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_SKIN, skin);
  dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_BIO, player.bio ?? '');
  dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_XP, typeof player.xp === 'number' ? player.xp : 0);
  // Load the server inventory (purchased shop items) so the Bag actually shows them. Keep the local
  // starter items only when the account has no server inventory yet, so brand-new players can catch.
  if (Array.isArray(view.inventory) && view.inventory.length > 0) {
    dataManager.store.set(
      DATA_MANAGER_STORE_KEYS.INVENTORY,
      view.inventory.map((row) => ({ item: { id: row.itemId }, quantity: row.qty })),
    );
  }
  if (typeof player.gold === 'number') {
    dataManager.setGold(player.gold);
  }
  // ── Party hydration (server autosave is the source of truth) ──
  // The local save (MONSTER_TAMER_DATA) is a single global localStorage key shared by ALL wallets on
  // this browser. So we must be careful WHOSE party we keep:
  //   1. Server has a party (partyJson non-empty) → authoritative, use it.
  //   2. No server party, but the local party belongs to a DIFFERENT wallet → CLEAR it so this account
  //      gets a fresh starter (in world create) instead of inheriting the previous wallet's beasts
  //      (which autosave would then write onto THIS account — the cross-wallet corruption bug).
  //   3. No server party and the local party is ours (or ownership unknown — migration) → keep it.
  const wallet = netManager?.wallet ?? player.wallet ?? null;
  const serverParty = view?.partyJson;
  if (Array.isArray(serverParty) && serverParty.length > 0) {
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY, serverParty);
  } else if (wallet) {
    try {
      const prevOwner = localStorage.getItem(PARTY_OWNER_KEY);
      if (prevOwner && prevOwner !== wallet) {
        dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY, []);
      }
    } catch {
      /* localStorage unavailable — leave the party as-is */
    }
  }
  // ── PC box hydration ──
  // Server is authoritative when present (caught-while-full monsters persist across devices). When the
  // server has none and the local box belongs to a DIFFERENT wallet, clear it (same shared-localStorage
  // guard as the party) so this account never inherits the previous wallet's stored beasts.
  const serverStorage = view?.storageJson;
  if (Array.isArray(serverStorage)) {
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_STORAGE, serverStorage);
  } else if (wallet) {
    try {
      const prevOwner = localStorage.getItem(PARTY_OWNER_KEY);
      if (prevOwner && prevOwner !== wallet) {
        dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_STORAGE, []);
      }
    } catch {
      /* localStorage unavailable — leave the box as-is */
    }
  }
  // Record the current owner so a later wallet switch on this browser is detected.
  if (wallet) {
    try {
      localStorage.setItem(PARTY_OWNER_KEY, wallet);
    } catch {
      /* ignore */
    }
  }
  dataManager.saveData();
}

/**
 * Read the current account profile from the data-manager (used by the in-world profile page).
 * @returns {{ name: string; avatar: string; bio: string; gold: number }}
 */
export function readProfile() {
  return {
    name: dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_NAME) || '',
    avatar: resolveSkinKey(dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_AVATAR)),
    bio: dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_BIO) || '',
    gold: dataManager.getGold(),
  };
}

/**
 * Write edited profile fields back into the data-manager + localStorage (keeps the local
 * skin in sync with the chosen avatar). Called after a successful api profile save.
 * @param {{ name?: string; avatar?: string; bio?: string }} patch
 * @returns {void}
 */
export function writeProfile(patch) {
  if (patch.name !== undefined) {
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_NAME, patch.name);
  }
  if (patch.avatar !== undefined) {
    const skin = resolveSkinKey(patch.avatar);
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_AVATAR, skin);
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_SKIN, skin);
  }
  if (patch.bio !== undefined) {
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_BIO, patch.bio);
  }
  dataManager.saveData();
}
